import { z } from "zod";
import { detectCardBrand } from "@/utils/cardUtils";
import {
  CreditCardSchema,
  CreditCardHolderInfoSchema,
  CreditCardInfoSchema,
} from "./creditCard";

export const BillingTypeSchema = z.enum([
  "PIX",
  "BOLETO",
  "CREDIT_CARD",
  "DEBIT_CARD",
  "UNDEFINED",
  "TED",
]);
export type BillingType = z.infer<typeof BillingTypeSchema>;

export const CreatePaymentAsaasSchema = z.object({
  saleId: z.string(),
  customer: z.string(),
  billingType: BillingTypeSchema,
  value: z.number().optional(),
  receiptUrl: z.string().optional(),
  description: z.string().optional(),
  installmentCount: z.number().optional(),
  totalValue: z.number().optional(),
});

export const CreateCreditCardPaymentAsaasSchema = z.object({
  saleId: z.string(),
  customer: z.string(),
  billingType: z.literal("CREDIT_CARD"),
  creditCardToken: z.string().optional(),
  creditCard: CreditCardSchema.optional(),
  creditCardHolderInfo: CreditCardHolderInfoSchema.optional(),
  remoteIp: z.string(),
  installmentCount: z.number().optional(),
  totalValue: z.number().optional(),
  value: z.number().optional(),
});

export type CreatePaymentAsaasDTO = z.infer<typeof CreatePaymentAsaasSchema>;
export type CreateCreditCardPaymentAsaasDTO = z.infer<
  typeof CreateCreditCardPaymentAsaasSchema
>;

export const PixResponseAsaasSchema = z.object({
  encodedImage: z.string(),
  payload: z.string(),
  expirationDate: z.string(),
});

export type PixResponseAsaasDTO = z.infer<typeof PixResponseAsaasSchema>;

const BasePaymentResponseSchema = z.object({
  id: z.string(),
  object: z.literal("payment"),
  dateCreated: z.string(),
  customer: z.string(),
  paymentLink: z.string().nullable().optional(),
  value: z.number(),
  netValue: z.number(),
  status: z.string(),
  dueDate: z.string(),
  originalDueDate: z.string(),
  paymentDate: z.string().nullable().optional(),
  clientPaymentDate: z.string().nullable().optional(),
  installmentNumber: z.number().nullable().optional(),
  invoiceUrl: z.string(),
  invoiceNumber: z.string(),
  externalReference: z.string().optional(),
  deleted: z.boolean(),
  bankSlipUrl: z.string().nullable().optional(),
  transactionReceiptUrl: z.string().nullable().optional(),
  nossoNumero: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  discount: z.any().optional(),
  fine: z.any().optional(),
  interest: z.any().optional(),
  postalService: z.boolean(),
  creditCard: CreditCardInfoSchema.optional(),
});

export const TedResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  billingType: z.literal("TED"),
  status: z.literal("TED"),
  receiptUrl: z.string(),
});

export const PaymentResponseAsaasSchema = z.union([
  PixResponseAsaasSchema,
  TedResponseSchema,
  BasePaymentResponseSchema.extend({
    billingType: z.literal("BOLETO"),
  }),
  BasePaymentResponseSchema.extend({
    billingType: z.literal("CREDIT_CARD"),
  }),
  BasePaymentResponseSchema.extend({
    billingType: z.literal("DEBIT_CARD"),
  }),
  BasePaymentResponseSchema.extend({
    billingType: z.literal("UNDEFINED"),
  }),
  BasePaymentResponseSchema.extend({
    billingType: z.literal("PIX"),
    pixQrCode: PixResponseAsaasSchema.optional(),
  }),
]);

export type PaymentResponseAsaasDTO = z.infer<
  typeof PaymentResponseAsaasSchema
>;

export const CreditCardFormSchema = z
  .object({
    creditCard: CreditCardSchema,
    creditCardHolderInfo: CreditCardHolderInfoSchema,
    installments: z.string().min(1, "Parcelas são obrigatórias."),
  })
  .superRefine((data, ctx) => {
    const brand = detectCardBrand(data.creditCard.number);
    const digits = data.creditCard.ccv.replace(/\D/g, "");

    if (brand === "amex") {
      if (digits.length !== 4) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "CVV inválido.",
          path: ["creditCard", "ccv"],
        });
      }
    } else if (digits.length !== 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "CVV inválido.",
        path: ["creditCard", "ccv"],
      });
    }
  });

export type CreditCardFormDTO = z.infer<typeof CreditCardFormSchema>;
